import React, { useMemo, useState } from 'react';
import { DeviceIcon } from '@/components/DeviceIcon';
import { Input } from '@/components/ui/input';
import { useI18n } from '@/i18n/I18nContext';
import { useStore, getActivePage } from '@/state/store';
import { pageDisplayName } from '@/lib/pageNames';
import { deviceLabel } from '@/lib/deviceIcons';
import { standardPages } from '@/lib/detail';
import { CONNECTION_TYPES, connLabel } from '@/lib/connections';
import { Search, Plus, Check, Layers, MapPin } from 'lucide-react';

// Left sidebar shown on detail pages (replaces the device palette + the old
// toolbar dropdown). Lists every device created on the standard pages; clicking
// an available device adds it to the active detail page. Already-added devices
// are greyed out and instead select the device when clicked.
export const DetailDeviceList = () => {
  const { t, language } = useI18n();
  const [q, setQ] = useState('');
  const doc = useStore((s) => s.doc);
  const page = useStore(getActivePage);
  const addDetailMember = useStore((s) => s.addDetailMember);
  const addLocationMarker = useStore((s) => s.addLocationMarker);
  const selectNode = useStore((s) => s.selectNode);
  const selectedNodeId = useStore((s) => s.selectedNodeId);
  const activeConnType = useStore((s) => s.activeConnType);
  const setActiveConnType = useStore((s) => s.setActiveConnType);

  const isLoc = page?.type === 'location';
  const memberIds = useMemo(
    () => new Set(((isLoc ? page?.markers : page?.members) || []).map((m) => m.deviceId)),
    [page, isLoc]
  );

  const devices = useMemo(() => {
    const list = [];
    for (const p of standardPages(doc)) {
      for (const n of p.nodes || []) {
        if (n.data?.aliasOf) continue; // skip alias placements — only master devices
        list.push({
          id: n.id,
          name: n.data?.label || deviceLabel(n.data?.deviceType, language),
          deviceType: n.data?.deviceType,
          page: pageDisplayName(p.name, language),
        });
      }
    }
    return list;
  }, [doc, language]);

  const filtered = useMemo(
    () => devices.filter((d) => d.name.toLowerCase().includes(q.trim().toLowerCase())),
    [devices, q]
  );

  const onItemClick = (d) => {
    if (!page) return;
    if (memberIds.has(d.id)) selectNode(d.id);
    else if (isLoc) addLocationMarker(page.id, d.id);
    else addDetailMember(page.id, d.id);
  };

  return (
    <div className="flex h-full flex-col bg-[#FBFBFA]" data-testid="detail-device-list">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
        {isLoc ? <MapPin className="h-3.5 w-3.5 text-muted-foreground" /> : <Layers className="h-3.5 w-3.5 text-muted-foreground" />}
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {isLoc ? t('location_add_overview') : t('detail_device_overview')}
        </span>
      </div>

      {devices.length > 0 && (
        <div className="px-3 py-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="detail-device-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('search_devices')}
              className="h-9 pl-8 text-sm"
            />
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto px-2 pb-3">
        {devices.length === 0 ? (
          <div
            className="mx-1 mt-2 rounded-md border border-dashed border-border bg-white/60 px-3 py-4 text-center"
            data-testid="detail-overview-empty"
          >
            <p className="text-[11px] leading-snug text-muted-foreground">{t('detail_overview_empty')}</p>
          </div>
        ) : (
          <div className="space-y-1 pt-0.5">
            {filtered.map((d) => {
              const added = memberIds.has(d.id);
              const selected = selectedNodeId === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => onItemClick(d)}
                  data-testid={`detail-overview-item-${d.id}`}
                  data-added={added ? 'true' : 'false'}
                  title={added ? t('detail_added_badge') : isLoc ? t('location_add_to_page') : t('detail_add_to_page')}
                  className={`group flex w-full items-center gap-2.5 rounded-md border px-2 py-2 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0B5FFF]/40 ${
                    added
                      ? 'border-transparent bg-[#1b232c]/[0.05] hover:bg-[#1b232c]/[0.08]'
                      : selected
                      ? 'border-[#0B5FFF]/40 bg-white shadow-sm'
                      : 'border-border bg-white hover:border-[#0B5FFF]/40 hover:shadow-sm'
                  }`}
                >
                  <div className={`flex h-8 w-8 shrink-0 items-center justify-center ${added ? 'opacity-50' : ''}`}>
                    <DeviceIcon type={d.deviceType} size={28} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`truncate text-[12.5px] font-medium ${added ? 'text-muted-foreground' : 'text-[#111418]'}`}>
                      {d.name}
                    </div>
                    <div className="truncate text-[10.5px] text-muted-foreground">{d.page}</div>
                  </div>
                  {added ? (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#0F7B6C]/10 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-[#0F7B6C]">
                      <Check className="h-3 w-3" /> {t('detail_added_badge')}
                    </span>
                  ) : (
                    <Plus className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
                  )}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="px-2 py-3 text-[11px] text-muted-foreground" data-testid="detail-overview-no-match">
                {t('no_devices')}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-border px-3 py-2.5" data-testid="detail-connection-legend">
        {isLoc ? (
          <p className="text-[11px] leading-snug text-muted-foreground">{t('location_empty')}</p>
        ) : (
          <>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('connections')}</span>
            </div>
            <div className="space-y-1">
              {CONNECTION_TYPES.map((c) => {
                const active = c.key === activeConnType;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setActiveConnType(c.key)}
                    data-testid={`detail-conn-type-${c.key}`}
                    className={`flex w-full items-center gap-2.5 rounded-md border px-2 py-1.5 text-left text-xs transition-colors ${
                      active ? 'border-[#0B5FFF]/40 bg-white shadow-sm' : 'border-transparent hover:bg-white/70'
                    }`}
                  >
                    <svg width="34" height="10" className="shrink-0">
                      <line x1="1" y1="5" x2="33" y2="5" stroke={c.color} strokeWidth={c.width} strokeDasharray={c.dash || undefined} />
                    </svg>
                    <span className={`truncate ${active ? 'font-semibold text-[#111418]' : 'text-[#2A3138]'}`}>
                      {connLabel(c.key, language)}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-[11px] leading-snug text-muted-foreground">{t('detail_connect_hint')}</p>
          </>
        )}
      </div>
    </div>
  );
};
